import { Square2StackIcon } from "@heroicons/react/20/solid";
import {
  Paper,
  Text,
  Tooltip,
} from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { useState, useRef, useEffect } from "react";

interface SmallTextProps {
  text: string;
  width?: string | number;
  showCopy?: boolean;
  size?: string;
  className?: string;
}

const SmallTextWithCopy = ({ text, width = "100%", showCopy = true, size = "md", className = "" }: SmallTextProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [copyPopupVisible, setCopyPopupVisible] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const clipboard = useClipboard();

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };
    
    checkTruncation();
    // Add resize listener to check truncation on window resize
    window.addEventListener("resize", checkTruncation);
    
    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, [text, width]);

  const handleCopy = () => {
    clipboard.copy(text);
    setCopyPopupVisible(true);
    setTimeout(() => {
      setCopyPopupVisible(false);
    }, 2000); // Hide the popup after 2 seconds
  };

  return (
    <Paper className={`flex items-center justify-center gap-2 ${className}`}>
      <Tooltip
        label={text}
        disabled={!isTruncated}
        position="bottom"
        withArrow
        color="dark"
        multiline
        maw={300}
        transitionProps={{ transition: "pop", duration: 200 }}
      >
        <Text 
          fz={size} 
          ff="Nunito" 
          truncate 
          ref={textRef}
          className="font-semibold text-secondary-500" 
          style={{ flex: 1, width, maxWidth: width }}
        >
          {text}
        </Text>
      </Tooltip>
      
      {showCopy && (
        <Tooltip
          opened={copyPopupVisible}
          position="right"
          color="text.5"
          label="Copied!"
          offset={3}
          withArrow
        >
          <button type="button" onClick={handleCopy} className="text-gray-400 hover:text-purple-700">
            <Square2StackIcon width={15} height={15} />
          </button>
        </Tooltip>
      )}
    </Paper>
  );
};

export default SmallTextWithCopy;
